import { FormControl } from "@chakra-ui/form-control";
import { ArrowBackIcon, SearchIcon, DeleteIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { Input } from "@chakra-ui/input";
import { Box, Text } from "@chakra-ui/layout";
import {
  IconButton,
  Spinner,
  useToast,
  Button,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  getSender,
  getSenderFull,
  isTypingForChat,
  decodeStoredContent,
  formatLastSeen,
} from "../config/ChatLogics";
import ScrollableChat from "./ScrollableChat";
import GroupChatAvatar from "./userAvatar/GroupChatAvatar";
import ChatLoading from "./ChatLoading";
import ProfileModal from "./miscellaneous/ProfileModal";
import "./styles.css";

import io from "socket.io-client";
import { ChatState } from "../Context/ChatProvider";
import UpdateGroupChatModal from "./miscellaneous/UpdateGroupChatModal";

const ENDPOINT =
  process.env.REACT_APP_ENDPOINT ||
  (process.env.NODE_ENV === "production"
    ? window.location.origin
    : "http://localhost:5000");

var socket, selectedChatCompare;

const MESSAGES_PAGE_SIZE = 50;

const SingleChat = ({ fetchAgain, setFetchAgain }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [socketConnected, setSocketConnected] = useState(false);
  const [typing, setTyping] = useState(false);
  const [istyping, setIsTyping] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [forwarding, setForwarding] = useState(null);
  const [forwardTarget, setForwardTarget] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [peerPresence, setPeerPresence] = useState(null);
  const [deletingChat, setDeletingChat] = useState(false);
  const typingTimer = useRef(null);
  const isTypingTimer = useRef(null);
  const hasConnectedOnce = useRef(false);
  const searchTimeout = useRef(null);
  const chatsRef = useRef(null);
  const userIdRef = useRef(null);
  const blockedIdsRef = useRef([]);
  const [muting, setMuting] = useState(false);
  const [sending, setSending] = useState(false);
  const composerRef = useRef(null);
  const toast = useToast();
  const {
    isOpen: isForwardOpen,
    onOpen: onForwardOpen,
    onClose: onForwardClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const {
    selectedChat,
    setSelectedChat,
    user,
    setNotification,
    setChats,
    chats,
    presenceMap,
    setPresenceMap,
    blockedIds,
  } = ChatState();

  const bumpFetchAgain = () => {
    if (typeof setFetchAgain === "function") {
      setFetchAgain((prev) => (typeof prev === "number" ? prev + 1 : !prev));
    }
  };

  const emitStopTyping = (roomId) => {
    const id = roomId || selectedChat?._id;
    if (socket && id) socket.emit("stop typing", id);
    if (typingTimer.current) {
      clearTimeout(typingTimer.current);
      typingTimer.current = null;
    }
    setTyping(false);
  };

  const scheduleStopTyping = () => {
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      emitStopTyping();
    }, 3000);
  };

  const markChatAsRead = async (chatId) => {
    if (!chatId) return;
    let token = null;
    try {
      token = JSON.parse(localStorage.getItem("userInfo"))?.token;
    } catch {
      token = null;
    }
    if (!token) return;
    try {
      await axios.put(
        "/api/message/read",
        { chatId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (typeof setChats === "function") {
        setChats((prev) =>
          Array.isArray(prev)
            ? prev.map((c) =>
                String(c._id) === String(chatId)
                  ? { ...c, unreadCount: 0 }
                  : c
              )
            : prev
        );
      }
      if (socket) socket.emit("messages read", { chatId });
    } catch {
      // best-effort
    }
  };

  const fetchMessages = async (pageNum = 1, appendOlder = false) => {
    if (!selectedChat) return;
    if (!user?.token) return;

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      };

      if (appendOlder) setLoadingMore(true);
      else setLoading(true);

      const { data } = await axios.get(
        `/api/message/${selectedChat._id}?page=${pageNum}&limit=${MESSAGES_PAGE_SIZE}`,
        config
      );
      const list = Array.isArray(data) ? data : data.messages || [];
      if (appendOlder) {
        setMessages((prev) => [...list, ...prev]);
      } else {
        setMessages(list);
      }
      setPage(pageNum);
      setHasMore(list.length === MESSAGES_PAGE_SIZE);
      setLoading(false);
      setLoadingMore(false);

      if (!appendOlder) socket.emit("join chat", selectedChat._id);
    } catch (error) {
      setLoading(false);
      setLoadingMore(false);
      toast({
        title: "Error Occured!",
        description: "Failed to Load the Messages",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const loadEarlier = () => {
    if (loadingMore || !hasMore) return;
    fetchMessages(page + 1, true);
  };

  // Submit the composer draft. The draft + reply context are captured up
  // front and cleared ONLY on 200 — a failed POST keeps everything in place
  // so nothing the user typed is ever lost.
  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    if (!selectedChat?._id || !user?.token) return;
    // Locked 1-1 chats (blocked either way) can't send; the server
    // enforces this too, this just avoids a wasted request.
    if (
      !selectedChat.isGroupChat &&
      (peerIsBlocked || selectedChat.peerBlockedMe)
    ) {
      toast({
        title: selectedChat.peerBlockedMe
          ? "You have been blocked — message not sent"
          : "Unblock this user to send messages",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    const chatId = selectedChat._id;
    const textToSend = newMessage;
    const replyToSend = replyingTo;
    emitStopTyping(chatId);
    setSending(true);
    try {
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      };
      const payload = {
        content: textToSend,
        chatId,
      };
      if (replyToSend?._id) payload.replyTo = replyToSend._id;
      const { data } = await axios.post("/api/message", payload, config);
      socket.emit("new message", data);
      // The user may have switched threads mid-flight: only append when
      // this chat is still open. The list preview refreshes regardless.
      if (
        selectedChatCompare &&
        String(selectedChatCompare._id) === String(chatId)
      ) {
        setMessages((prev) => [...prev, data]);
      }
      // Clear only what this send consumed, so text typed mid-flight survives.
      setNewMessage((prev) => (prev === textToSend ? "" : prev));
      setReplyingTo((prev) =>
        prev?._id === replyToSend?._id ? null : prev
      );
      bumpFetchAgain();
    } catch (error) {
      // Draft + reply context untouched — user can retry as-is.
      toast({
        title: "Error Occured!",
        description:
          error.response?.data?.message || error.message || "Failed to send",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setSending(false);
    }
  };

  const sendViaButton = () => {
    sendMessage();
  };

  // Textarea keys: Enter sends, Shift+Enter inserts a newline.
  const composerKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleEditStart = (m) => {
    setEditingId(String(m._id));
    setEditDraft(typeof m.content === "string" ? m.content : "");
  };

  const handleSaveEdit = async (m) => {
    const id = m._id;
    const trimmed = (editDraft || "").trim();
    if (!trimmed) {
      toast({
        title: "Message cannot be empty",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
      return;
    }
    try {
      const config = {
        headers: { Authorization: `Bearer ${user?.token}` },
      };
      const { data } = await axios.put(
        `/api/message/${id}`,
        { content: trimmed },
        config
      );
      setMessages((prev) => prev.map((x) => (String(x._id) === String(id) ? data : x)));
      setEditingId(null);
      setEditDraft("");
      socket.emit("message edited", data);
      bumpFetchAgain();
    } catch (error) {
      toast({
        title: "Failed to edit message",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const handleDelete = async (m) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${user?.token}` },
      };
      const { data } = await axios.delete(`/api/message/${m._id}`, config);
      setMessages((prev) =>
        prev.map((x) => (String(x._id) === String(m._id) ? data : x))
      );
      socket.emit("message deleted", data);
      bumpFetchAgain();
    } catch (error) {
      toast({
        title: "Failed to delete message",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const handleReact = async (messageId, emoji) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${user?.token}` },
      };
      const { data } = await axios.put(
        `/api/message/${messageId}/react`,
        { emoji },
        config
      );
      setMessages((prev) =>
        prev.map((x) => (String(x._id) === String(messageId) ? data : x))
      );
      socket.emit("message reacted", data);
    } catch (error) {
      toast({
        title: "Failed to react",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const handleForward = (m) => {
    setForwarding(m);
    setForwardTarget(selectedChat?._id || "");
    onForwardOpen();
  };

  const confirmForward = async () => {
    if (!forwarding || !forwardTarget || !user?.token) return;
    try {
      const config = {
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      };
      const { data } = await axios.post(
        "/api/message",
        {
          content: forwarding.isDeleted
            ? "Forwarded message"
            : forwarding.content,
          chatId: forwardTarget,
          isForwarded: true,
          forwardedFrom: selectedChat?._id,
        },
        config
      );
      socket.emit("new message", data);
      if (String(forwardTarget) === String(selectedChat?._id)) {
        setMessages((prev) => [...prev, data]);
      }
      bumpFetchAgain();
      onForwardClose();
      setForwarding(null);
      toast({
        title: "Message forwarded",
        status: "success",
        duration: 2500,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      toast({
        title: "Failed to forward",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
  };

  const handleSearchInput = (value) => {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!value.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        setSearching(true);
        const config = {
          headers: { Authorization: `Bearer ${user?.token}` },
        };
        const { data } = await axios.get(
          `/api/message/search?q=${encodeURIComponent(
            value.trim()
          )}&chatId=${selectedChat?._id}&limit=20`,
          config
        );
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (error) {
        toast({
          title: "Search failed",
          description: error.response?.data?.message || error.message,
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleDeleteChat = async () => {
    if (!selectedChat?._id) return;
    try {
      setDeletingChat(true);
      const config = {
        headers: { Authorization: `Bearer ${user?.token}` },
      };
      await axios.delete(`/api/chat/${selectedChat._id}`, config);
      onDeleteClose();
      setSelectedChat(null);
      bumpFetchAgain();
      toast({
        title: "Chat deleted",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      toast({
        title: "Failed to delete chat",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setDeletingChat(false);
    }
  };

  const peerUser = !selectedChat?.isGroupChat
    ? getSenderFull(user, selectedChat?.users)
    : null;
  const peerId = peerUser?._id ? String(peerUser._id) : null;
  const peerPresenceEntry = peerId ? presenceMap?.[peerId] : null;
  const presenceLabel = !selectedChat?.isGroupChat
    ? formatLastSeen(
        peerPresenceEntry?.isOnline ?? peerUser?.isOnline,
        peerPresenceEntry?.lastSeen ?? peerUser?.lastSeen ?? peerPresence?.lastSeen
      )
    : `${selectedChat?.users?.length || 0} members`;

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    userIdRef.current = user?._id ? String(user._id) : null;
  }, [user?._id]);

  useEffect(() => {
    blockedIdsRef.current = Array.isArray(blockedIds) ? blockedIds : [];
  }, [blockedIds]);

  // Auto-grow the composer as the draft wraps, capped so it never
  // eats the thread. Resets when the draft clears or the chat changes.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  }, [newMessage, selectedChat?._id]);

  useEffect(() => {
    socket = io(ENDPOINT);
    if (user) {
      socket.emit("setup", user);
    }
    socket.on("connected", () => setSocketConnected(true));
    socket.on("connect", () => {
      try {
        const stored = localStorage.getItem("userInfo");
        const info = stored ? JSON.parse(stored) : null;
        if (info) socket.emit("setup", info);
      } catch {
        // corrupt storage handled by auth flow
      }
      if (selectedChatCompare?._id) {
        socket.emit("join chat", selectedChatCompare._id);
      }
      if (hasConnectedOnce.current) {
        bumpFetchAgain();
      }
      hasConnectedOnce.current = true;
    });
    socket.on("typing", (roomId) => {
      if (!isTypingForChat(roomId, selectedChatCompare)) return;
      setIsTyping(true);
      if (isTypingTimer.current) clearTimeout(isTypingTimer.current);
      isTypingTimer.current = setTimeout(() => setIsTyping(false), 10000);
    });
    socket.on("stop typing", (roomId) => {
      if (!isTypingForChat(roomId, selectedChatCompare)) return;
      if (isTypingTimer.current) {
        clearTimeout(isTypingTimer.current);
        isTypingTimer.current = null;
      }
      setIsTyping(false);
    });
    socket.on("messages read", (payload) => {
      const chatId =
        payload && typeof payload === "object" ? payload.chatId : payload;
      if (!chatId) return;
      const readerId =
        payload && typeof payload === "object" ? payload.readerId : null;
      if (readerId && typeof setChats === "function") {
        setChats((prev) =>
          Array.isArray(prev)
            ? prev.map((c) => {
                if (String(c._id) !== String(chatId)) return c;
                const lm = c.latestMessage;
                if (!lm) return c;
                const readBy = Array.isArray(lm.readBy) ? lm.readBy : [];
                if (
                  readBy.some(
                    (id) => String(id && id._id ? id._id : id) === String(readerId)
                  )
                )
                  return c;
                return {
                  ...c,
                  latestMessage: { ...lm, readBy: [...readBy, readerId] },
                };
              })
            : prev
        );
      }
      bumpFetchAgain();
    });

    const upsertMessage = (updated) => {
      if (!updated?._id) return;
      const chatId =
        updated.chat && typeof updated.chat === "object"
          ? updated.chat._id
          : updated.chat;
      if (
        selectedChatCompare &&
        String(chatId) !== String(selectedChatCompare._id)
      ) {
        return;
      }
      setMessages((prev) =>
        prev.some((m) => String(m._id) === String(updated._id))
          ? prev.map((m) => (String(m._id) === String(updated._id) ? updated : m))
          : [...prev, updated]
      );
    };
    socket.on("message edited", upsertMessage);
    socket.on("message deleted", upsertMessage);
    socket.on("message reacted", upsertMessage);
    socket.on("presence update", (p) => {
      if (!p?.userId) return;
      if (typeof setPresenceMap === "function") {
        setPresenceMap((prev) => ({
          ...prev,
          [String(p.userId)]: {
            isOnline: !!p.isOnline,
            lastSeen: p.lastSeen,
          },
        }));
      }
      if (peerId && String(p.userId) === String(peerId)) {
        setPeerPresence({ isOnline: !!p.isOnline, lastSeen: p.lastSeen });
      }
    });

    const getMyId = () => {
      if (userIdRef.current) return String(userIdRef.current);
      try {
        const stored = localStorage.getItem("userInfo");
        const info = stored ? JSON.parse(stored) : null;
        if (info?._id) return String(info._id);
      } catch {
        // ignore corrupt storage
      }
      return null;
    };

    const idOf = (u) => String((u && u._id) || u);

    const upsertChatToTop = (chat) => {
      if (!chat?._id || typeof setChats !== "function") return;
      const id = String(chat._id);
      setChats((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const rest = list.filter((c) => String(c._id) !== id);
        return [chat, ...rest];
      });
    };

    // Someone created a group that includes me (or my own create echo).
    // Instant list update so the new group appears without a manual refresh.
    socket.on("group created", (chat) => {
      if (!chat?._id) return;
      const known = (chatsRef.current || []).some(
        (c) => String(c._id) === String(chat._id)
      );
      upsertChatToTop(chat);
      if (!known) {
        toast({
          title: `Added to new group: ${chat.chatName || "Group"}`,
          status: "info",
          duration: 4000,
          isClosable: true,
          position: "bottom-left",
        });
      }
    });

    // Rename / add / remove / admin transfer for a group I'm still in.
    // Payload is the populated chat; diff against the cached copy to say
    // what actually changed (who joined / left) instead of a generic toast.
    socket.on("group updated", (chat) => {
      if (!chat?._id) return;
      const old = (chatsRef.current || []).find(
        (c) => String(c._id) === String(chat._id)
      );
      upsertChatToTop(chat);
      if (
        selectedChatCompare &&
        String(selectedChatCompare._id) === String(chat._id)
      ) {
        setSelectedChat(chat);
      }
      if (!old) return;
      try {
        const oldIds = new Set((old.users || []).map(idOf));
        const newIds = new Set((chat.users || []).map(idOf));
        const joined = (chat.users || []).filter((u) => !oldIds.has(idOf(u)));
        const left = (old.users || []).filter((u) => !newIds.has(idOf(u)));
        const oldAdmin = String(
          (old.groupAdmin && old.groupAdmin._id) || old.groupAdmin || ""
        );
        const newAdmin = String(
          (chat.groupAdmin && chat.groupAdmin._id) || chat.groupAdmin || ""
        );
        if (joined.length > 0) {
          const names = joined.map((u) => u.name || "Someone").join(", ");
          toast({
            title: `${names} joined "${chat.chatName || "Group"}"`,
            status: "info",
            duration: 4000,
            isClosable: true,
            position: "bottom-left",
          });
        } else if (left.length > 0) {
          const names = left.map((u) => u.name || "Someone").join(", ");
          toast({
            title: `${names} left "${chat.chatName || "Group"}"`,
            status: "info",
            duration: 4000,
            isClosable: true,
            position: "bottom-left",
          });
        } else if (old.chatName !== chat.chatName) {
          toast({
            title: `Group renamed to "${chat.chatName}"`,
            status: "info",
            duration: 3000,
            isClosable: true,
            position: "bottom-left",
          });
        } else if (oldAdmin !== newAdmin) {
          const adminUser = (chat.users || []).find(
            (u) => idOf(u) === newAdmin
          );
          toast({
            title: `${adminUser?.name || "Someone"} is now admin of "${
              chat.chatName || "Group"
            }"`,
            status: "info",
            duration: 4000,
            isClosable: true,
            position: "bottom-left",
          });
        } else if (
          (old.description || "") !== (chat.description || "") ||
          (old.pic || "") !== (chat.pic || "")
        ) {
          toast({
            title: `Group profile updated for "${chat.chatName || "Group"}"`,
            status: "info",
            duration: 3000,
            isClosable: true,
            position: "bottom-left",
          });
        }
      } catch {
        // toast content is best-effort; the list/selected chat already updated
      }
    });

    // I was removed (or I left on another device): drop the chat everywhere.
    socket.on("removed from group", ({ chatId, removedUserId } = {}) => {
      if (!chatId || !removedUserId) return;
      if (String(removedUserId) !== String(getMyId())) return;
      const old = (chatsRef.current || []).find(
        (c) => String(c._id) === String(chatId)
      );
      if (typeof setChats === "function") {
        setChats((prev) =>
          Array.isArray(prev)
            ? prev.filter((c) => String(c._id) !== String(chatId))
            : prev
        );
      }
      if (
        selectedChatCompare &&
        String(selectedChatCompare._id) === String(chatId)
      ) {
        setSelectedChat(null);
        setMessages([]);
      }
      toast({
        title: old
          ? `You were removed from "${old.chatName || "Group"}"`
          : "You were removed from a group",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom-left",
      });
    });

    // Admin deleted the group (or last member left): remove it for everyone.
    socket.on("group deleted", ({ chatId } = {}) => {
      if (!chatId) return;
      const old = (chatsRef.current || []).find(
        (c) => String(c._id) === String(chatId)
      );
      if (typeof setChats === "function") {
        setChats((prev) =>
          Array.isArray(prev)
            ? prev.filter((c) => String(c._id) !== String(chatId))
            : prev
        );
      }
      if (
        selectedChatCompare &&
        String(selectedChatCompare._id) === String(chatId)
      ) {
        setSelectedChat(null);
        setMessages([]);
      }
      toast({
        title: old
          ? `Group "${old.chatName || "Group"}" was deleted`
          : "A group was deleted",
        status: "warning",
        duration: 4000,
        isClosable: true,
        position: "bottom-left",
      });
    });

    // Block state changed FOR me: someone blocked/unblocked me. Flip the
    // peerBlockedMe flag on every 1-1 chat with them so the composer
    // locks/unlocks live, without a refresh.
    const applyPeerBlockState = (blockerId, blocked) => {
      if (!blockerId) return;
      const idStr = String(blockerId);
      const hasPeer = (c) =>
        !c?.isGroupChat &&
        (c.users || []).some(
          (u) => String((u && u._id) || u) === idStr
        );
      const cached = (chatsRef.current || []).find(hasPeer);
      const peer =
        cached?.users?.find(
          (u) => String((u && u._id) || u) === idStr
        ) || null;
      if (typeof setChats === "function") {
        setChats((prev) =>
          Array.isArray(prev)
            ? prev.map((c) =>
                hasPeer(c) ? { ...c, peerBlockedMe: blocked } : c
              )
            : prev
        );
      }
      if (selectedChatCompare && hasPeer(selectedChatCompare)) {
        setSelectedChat({ ...selectedChatCompare, peerBlockedMe: blocked });
      }
      toast({
        title: blocked
          ? `You have been blocked by ${peer?.name || "a user"}`
          : `${peer?.name || "Someone"} unblocked you`,
        status: blocked ? "warning" : "success",
        duration: 4000,
        isClosable: true,
        position: "bottom-left",
      });
    };
    socket.on("user blocked", ({ blockerId } = {}) =>
      applyPeerBlockState(blockerId, true)
    );
    socket.on("user unblocked", ({ blockerId } = {}) =>
      applyPeerBlockState(blockerId, false)
    );

    const hb = setInterval(() => {
      if (socket) socket.emit("heartbeat");
    }, 30000);

    return () => {
      clearInterval(hb);
      if (typingTimer.current) clearTimeout(typingTimer.current);
      if (isTypingTimer.current) clearTimeout(isTypingTimer.current);
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      socket.off("connected");
      socket.off("connect");
      socket.off("typing");
      socket.off("stop typing");
      socket.off("messages read");
      socket.off("message edited");
      socket.off("message deleted");
      socket.off("message reacted");
      socket.off("presence update");
      socket.off("group created");
      socket.off("group updated");
      socket.off("removed from group");
      socket.off("group deleted");
      socket.off("user blocked");
      socket.off("user unblocked");
      socket.disconnect();
    };

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user && socket) {
      socket.emit("setup", user);
    }
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (socket && selectedChatCompare?._id) {
      socket.emit("stop typing", selectedChatCompare._id);
    }
    if (typingTimer.current) {
      clearTimeout(typingTimer.current);
      typingTimer.current = null;
    }
    if (isTypingTimer.current) {
      clearTimeout(isTypingTimer.current);
      isTypingTimer.current = null;
    }
    setTyping(false);
    setIsTyping(false);
    setReplyingTo(null);
    setEditingId(null);
    setEditDraft("");
    setShowSearch(false);
    setSearchQuery("");
    setSearchResults([]);
    setPage(1);
    setHasMore(false);
    setPeerPresence(null);

    fetchMessages(1, false);
    markChatAsRead(selectedChat?._id);
    if (selectedChat?._id) {
      setNotification((prev) =>
        Array.isArray(prev)
          ? prev.filter(
              (n) =>
                String(
                  n.chat && typeof n.chat === "object" ? n.chat._id : n.chat
                ) !== String(selectedChat._id)
            )
          : prev
      );
    }

    // Fetch peer presence for 1-1 chats.
    const peer = !selectedChat?.isGroupChat
      ? getSenderFull(user, selectedChat?.users)
      : null;
    if (peer?._id && user?.token) {
      axios
        .get(`/api/user/presence?ids=${peer._id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
        .then(({ data }) => {
          const entry = Array.isArray(data) ? data[0] : null;
          if (entry) {
            setPeerPresence({
              isOnline: !!entry.isOnline,
              lastSeen: entry.lastSeen,
            });
            if (typeof setPresenceMap === "function") {
              setPresenceMap((prev) => ({
                ...prev,
                [String(entry._id)]: {
                  isOnline: !!entry.isOnline,
                  lastSeen: entry.lastSeen,
                },
              }));
            }
          }
        })
        .catch(() => {});
    }

    selectedChatCompare = selectedChat;
    // eslint-disable-next-line
  }, [selectedChat]);

  useEffect(() => {
    const handler = (newMessageReceived) => {
      if (!newMessageReceived?._id) return;
      const incomingChatId =
        newMessageReceived.chat && typeof newMessageReceived.chat === "object"
          ? newMessageReceived.chat._id
          : newMessageReceived.chat;
      // Muted chats and blocked senders never raise notifications —
      // the list + badges still update, the bell stays silent.
      const cached = (chatsRef.current || []).find(
        (c) => String(c._id) === String(incomingChatId)
      );
      const senderId =
        newMessageReceived.sender &&
        typeof newMessageReceived.sender === "object"
          ? newMessageReceived.sender._id
          : newMessageReceived.sender;
      const isMuted = !!(cached && cached.isMuted);
      const isBlockedSender =
        !!senderId &&
        (blockedIdsRef.current || []).some(
          (id) => String(id) === String(senderId)
        );
      const silent = isMuted || isBlockedSender;
      if (
        !selectedChatCompare ||
        String(selectedChatCompare._id) !== String(incomingChatId)
      ) {
        if (!silent) {
          setNotification((prev) =>
            prev.some((n) => n._id === newMessageReceived._id)
              ? prev
              : [newMessageReceived, ...prev]
          );
        }
        bumpFetchAgain();
      } else {
        // Dedupe: server retries or dual-room delivery must never append twice.
        setMessages((prev) =>
          prev.some((m) => String(m._id) === String(newMessageReceived._id))
            ? prev
            : [...prev, newMessageReceived]
        );
        markChatAsRead(selectedChatCompare._id);
        bumpFetchAgain();
      }
    };
    socket.on("message received", handler);
    return () => {
      socket.off("message received", handler);
    };
    // eslint-disable-next-line
  }, []);

  const typingHandler = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    if (!socketConnected || !selectedChat?._id) return;

    if (value === "") {
      if (typing) emitStopTyping(selectedChat._id);
      return;
    }

    if (!typing) {
      setTyping(true);
      socket.emit("typing", selectedChat._id);
    }
    scheduleStopTyping();
  };

  const isGroupAdmin =
    !!selectedChat?.isGroupChat &&
    String(selectedChat?.groupAdmin?._id || selectedChat?.groupAdmin) ===
      String(user?._id);
  const canDeleteChat = selectedChat
    ? !selectedChat.isGroupChat || isGroupAdmin
    : false;

  const peerIsBlocked =
    !!peerUser?._id &&
    (blockedIds || []).some((id) => String(id) === String(peerUser._id));

  // Either direction locks the 1-1 composer: I blocked them, or they
  // blocked me (flag arrives via fetchChats + the live block events).
  const peerBlockedMe = !selectedChat?.isGroupChat && !!selectedChat?.peerBlockedMe;
  const chatLocked = peerIsBlocked || peerBlockedMe;

  const handleToggleMute = async () => {
    if (!selectedChat?._id || !user?.token) return;
    const next = !selectedChat.isMuted;
    try {
      setMuting(true);
      const config = {
        headers: { Authorization: `Bearer ${user.token}` },
      };
      const { data } = await axios.put(
        "/api/chat/mute",
        { chatId: selectedChat._id, mute: next },
        config
      );
      const muted = !!data.muted;
      setSelectedChat({ ...selectedChat, isMuted: muted });
      if (typeof setChats === "function") {
        setChats((prev) =>
          Array.isArray(prev)
            ? prev.map((c) =>
                String(c._id) === String(selectedChat._id)
                  ? { ...c, isMuted: muted }
                  : c
              )
            : prev
        );
      }
      toast({
        title: muted ? "Chat muted — bell stays silent" : "Chat unmuted",
        status: "success",
        duration: 2500,
        isClosable: true,
        position: "bottom",
      });
    } catch (error) {
      toast({
        title: "Failed to update mute",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    } finally {
      setMuting(false);
    }
  };

  return (
    <>
      {selectedChat ? (
        <>
          <Box
            py={2}
            px={3}
            w="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            className="thread-head"
          >
            <Box display="flex" alignItems="center" gap={2} minW={0} flex="1">
              <IconButton
                display={{ base: "flex", md: "none" }}
                icon={<ArrowBackIcon />}
                aria-label="Back to chats"
                variant="ghost"
                color="var(--ch-text)"
                _hover={{ bg: "var(--ch-hover-ghost)" }}
                onClick={() => setSelectedChat(null)}
              />
              {selectedChat.isGroupChat && (
                <GroupChatAvatar
                  users={selectedChat.users}
                  name={selectedChat.chatName}
                  pic={selectedChat.pic}
                  size={32}
                  currentUserId={user?._id}
                />
              )}
              <Box minW={0}>
                <Text
                  fontSize={{ base: "17px", md: "18px" }}
                  fontFamily="Sora"
                  fontWeight="700"
                  color="var(--ch-strong)"
                  isTruncated
                  className="thread-title"
                >
                  {messages &&
                    (!selectedChat.isGroupChat ? (
                      <>{getSender(user, selectedChat.users)}</>
                    ) : (
                      <>{(selectedChat.chatName || "").toUpperCase()}</>
                    ))}
                  {selectedChat.isMuted ? " 🔇" : ""}
                </Text>
                <Text fontSize="12px" className="thread-sub">
                  {!selectedChat.isGroupChat && peerId && (
                    <Box as="span" display="inline-flex" alignItems="center" gap={1}>
                      <Box
                        w="7px"
                        h="7px"
                        borderRadius="full"
                        bg={
                          (peerPresenceEntry?.isOnline ??
                          peerPresence?.isOnline ??
                          peerUser?.isOnline)
                            ? "#22c55e"
                            : "rgba(148,163,184,0.6)"
                        }
                        boxShadow="0 0 6px rgba(34,197,94,0.6)"
                      />
                      {presenceLabel}
                    </Box>
                  )}
                  {selectedChat.isGroupChat && presenceLabel}
                  {selectedChat.isGroupChat && selectedChat.description && (
                    <Box as="span" display="block" isTruncated>
                      {selectedChat.description}
                    </Box>
                  )}
                </Text>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                icon={<Text fontSize="13px">🔔</Text>}
                aria-label={selectedChat.isMuted ? "Unmute chat" : "Mute chat"}
                title={selectedChat.isMuted ? "Unmute chat" : "Mute chat"}
                variant="ghost"
                color={
                  selectedChat.isMuted
                    ? "var(--ch-muted-on)"
                    : "var(--ch-dim)"
                }
                _hover={{ bg: "var(--ch-hover-ghost)" }}
                onClick={handleToggleMute}
                isLoading={muting}
                size="sm"
              />
              <IconButton
                icon={<SearchIcon />}
                aria-label="Search in conversation"
                variant="ghost"
                color={showSearch ? "var(--ch-seen)" : "var(--ch-dim)"}
                _hover={{ bg: "var(--ch-hover-ghost)" }}
                onClick={() => setShowSearch((s) => !s)}
                size="sm"
              />
              {canDeleteChat && (
                <IconButton
                  icon={<DeleteIcon />}
                  aria-label="Delete chat"
                  variant="ghost"
                  color="var(--ch-danger)"
                  _hover={{ bg: "rgba(255,0,0,0.12)" }}
                  onClick={onDeleteOpen}
                  size="sm"
                />
              )}
              {messages &&
                (!selectedChat.isGroupChat ? (
                  <>
                    {getSenderFull(user, selectedChat.users) && (
                      <ProfileModal
                        user={getSenderFull(user, selectedChat.users)}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <UpdateGroupChatModal
                      fetchMessages={() => fetchMessages(1, false)}
                      fetchAgain={fetchAgain}
                      setFetchAgain={setFetchAgain}
                    />
                  </>
                ))}
            </Box>
          </Box>
          {showSearch && (
            <Box
              w="100%"
              px={2}
              pb={2}
              display="flex"
              flexDir="column"
              gap={2}
              bg="var(--ch-searchpanel)"
              borderRadius="lg"
              border="1px solid var(--ch-item-border)"
              p={2}
              mb={2}
            >
              <Input
                size="sm"
                className="chat-input"
                placeholder="Search messages in this chat…"
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
              />
              {searching && <Spinner size="sm" color="#22d3ee" />}
              {!searching && searchQuery.trim() && searchResults.length === 0 && (
                <Text fontSize="xs" color="var(--ch-faint)">
                  No matches.
                </Text>
              )}
              {searchResults.slice(0, 8).map((r) => (
                <Box
                  key={r._id}
                  p={2}
                  borderRadius="md"
                  bg="var(--ch-item)"
                  border="1px solid var(--ch-item-border)"
                >
                  <Text fontSize="xs" fontWeight="700" color="var(--ch-accent)">
                    {r.sender?.name} ·{" "}
                    {new Date(r.createdAt).toLocaleString()}
                  </Text>
                  <Text fontSize="sm" color="var(--ch-text)" noOfLines={2}>
                    {decodeStoredContent(r.content)}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
          <Box
            display="flex"
            flexDir="column"
            justifyContent="flex-end"
            p={3}
            w="100%"
            h="100%"
            borderRadius="lg"
            overflowY="hidden"
            className="thread-pane"
          >
            {loading ? (
              <ChatLoading variant="message" count={6} />
            ) : (
              <>
                {hasMore && (
                  <Button
                    size="xs"
                    variant="ghost"
                    color="#67e8f9"
                    _hover={{ bg: "rgba(255,255,255,0.08)" }}
                    onClick={loadEarlier}
                    isLoading={loadingMore}
                    mb={2}
                    alignSelf="center"
                  >
                    Load earlier messages
                  </Button>
                )}
                <div className="messages">
                  {messages.length === 0 ? (
                    <Box textAlign="center" py={8}>
                      <Text color="var(--ch-faint)" fontSize="sm">
                        No messages yet. Say hello 👋
                      </Text>
                    </Box>
                  ) : (
                    <ScrollableChat
                      messages={messages}
                      isGroupChat={!!selectedChat.isGroupChat}
                      isGroupAdmin={isGroupAdmin}
                      editingId={editingId}
                      editDraft={editDraft}
                      setEditDraft={setEditDraft}
                      onSaveEdit={handleSaveEdit}
                      onCancelEdit={() => {
                        setEditingId(null);
                        setEditDraft("");
                      }}
                      onEditStart={handleEditStart}
                      onDelete={handleDelete}
                      onReact={handleReact}
                      onReply={setReplyingTo}
                      onForward={handleForward}
                    />
                  )}
                </div>
              </>
            )}

            <FormControl
              id="first-name"
              isRequired
              mt={3}
            >
              {istyping ? (
                <div className="typing-bubble" aria-live="polite">
                  <span className="tdot" />
                  <span className="tdot" />
                  <span className="tdot" />
                </div>
              ) : null}
              {replyingTo && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  bg="rgba(34,211,238,0.1)"
                  border="1px solid rgba(34,211,238,0.25)"
                  borderRadius="lg"
                  px={3}
                  py={2}
                  mb={2}
                >
                  <Box minW={0}>
                    <Text fontSize="11px" fontWeight="800" color="var(--ch-accent)">
                      Replying to {replyingTo.sender?.name || "message"}
                    </Text>
                    <Text fontSize="12px" color="var(--ch-dim)" isTruncated>
                      {decodeStoredContent(replyingTo.content || "")}
                    </Text>
                  </Box>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="var(--ch-dim)"
                    onClick={() => setReplyingTo(null)}
                  >
                    ✕
                  </Button>
                </Box>
              )}
              <Box display="flex" gap={2} alignItems="flex-end" className="composer-bar">
                <Textarea
                  ref={composerRef}
                  variant="filled"
                  className="chat-input"
                  placeholder="Type a message"
                  value={newMessage}
                  onChange={typingHandler}
                  onKeyDown={composerKeyDown}
                  isDisabled={chatLocked}
                  rows={1}
                  resize="none"
                  overflowY="auto"
                />
                <IconButton
                  className="chat-btn-primary send-btn send-icon-btn"
                  aria-label="Send message"
                  title="Send"
                  icon={<ArrowForwardIcon />}
                  onClick={sendViaButton}
                  isDisabled={!newMessage.trim() || chatLocked}
                  isLoading={sending}
                />
              </Box>
              {peerIsBlocked && (
                <Text fontSize="xs" color="var(--ch-danger-text)" mt={2}>
                  You blocked {peerUser?.name || "this user"}. Unblock from
                  their profile to chat again.
                </Text>
              )}
              {peerBlockedMe && (
                <Text fontSize="xs" color="var(--ch-danger-text)" mt={2}>
                  You have been blocked by {peerUser?.name || "this user"}.
                </Text>
              )}
            </FormControl>
          </Box>
        </>
      ) : (
        <Box
          display="flex"
          flexDir="column"
          alignItems="center"
          justifyContent="center"
          h="100%"
          textAlign="center"
          px={6}
        >
          <Text
            fontSize="3xl"
            fontWeight="800"
            fontFamily="Sora"
            className="brand-gradient-text"
          >
            Pick a vibe to start chatting
          </Text>
          <Text fontSize="sm" color="var(--ch-faint)" mt={2}>
            Select a conversation from the left — messages land in under 50ms.
          </Text>
        </Box>
      )}

      <Modal isOpen={isForwardOpen} onClose={onForwardClose} isCentered size="md">
        <ModalOverlay bg="rgba(2,6,23,0.7)" backdropFilter="blur(6px)" />
        <ModalContent
          bg="var(--ch-modal)"
          color="var(--ch-text)"
          border="1px solid var(--ch-panel-border)"
          borderRadius="20px"
        >
          <ModalHeader fontFamily="Sora" fontWeight="800">
            Forward message
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="var(--ch-dim)" mb={2} noOfLines={3}>
              “{forwarding ? decodeStoredContent(forwarding.content) : ""}”
            </Text>
            <Text fontSize="xs" color="var(--ch-faint)" mb={2}>
              Choose a chat:
            </Text>
            <Box maxH="260px" overflowY="auto" className="dark-scroll" display="flex" flexDir="column" gap={1}>
              {(chats || []).map((c) => (
                <Box
                  key={c._id}
                  as="button"
                  textAlign="left"
                  p={2}
                  borderRadius="lg"
                  bg={String(forwardTarget) === String(c._id) ? "rgba(34,211,238,0.15)" : "var(--ch-item)"}
                  border="1px solid var(--ch-item-border)"
                  onClick={() => setForwardTarget(c._id)}
                  w="100%"
                >
                  <Text fontSize="sm" fontWeight="700" isTruncated>
                    {c.isGroupChat ? c.chatName : getSender(user, c.users)}
                  </Text>
                </Box>
              ))}
              {(!chats || chats.length === 0) && (
                <Text fontSize="sm" color="rgba(148,163,184,0.9)">
                  No chats available.
                </Text>
              )}
            </Box>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onForwardClose}>
              Cancel
            </Button>
            <Button
              className="chat-btn-primary"
              onClick={confirmForward}
              isDisabled={!forwardTarget}
            >
              Forward
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered size="sm">
        <ModalOverlay bg="rgba(2,6,23,0.7)" backdropFilter="blur(6px)" />
        <ModalContent
          bg="var(--ch-modal)"
          color="var(--ch-text)"
          border="1px solid var(--ch-panel-border)"
          borderRadius="20px"
        >
          <ModalHeader fontFamily="Sora" fontWeight="800" fontSize="lg">
            {selectedChat?.isGroupChat ? "Delete this group?" : "Delete this chat?"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="var(--ch-dim)">
              {selectedChat?.isGroupChat
                ? "This removes the group and all its messages for everyone. This cannot be undone."
                : "This deletes the chat and all its messages for you only. The other person keeps everything, and only new messages will reappear for you."}
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteChat}
              isLoading={deletingChat}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SingleChat;
